import { useEffect, useRef } from "react";

type Handler = (data: any) => void;

const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/**
 * Subscribes to the authenticated Server-Sent Events stream. EventSource cannot send an
 * Authorization header, so the stream is read with fetch and parsed manually.
 * Reconnects with backoff and resumes from the last received event id.
 */
export function useEventStream(dashboardId: string | undefined, handlers: Record<string, Handler>) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const controller = new AbortController();
    let lastEventId = "";
    let retry = 1000;
    let stopped = false;

    const dispatch = (block: string) => {
      let event = "message";
      let id = "";
      const data: string[] = [];
      for (const line of block.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("id:")) id = line.slice(3).trim();
        else if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
      }
      if (id) lastEventId = id;
      if (!data.length) return;
      try {
        handlersRef.current[event]?.(JSON.parse(data.join("\n")));
      } catch (error) {
        console.error("Invalid SSE payload", error);
      }
    };

    const connect = async () => {
      while (!stopped) {
        try {
          const token = localStorage.getItem("token");
          const base = process.env.REACT_APP_API_URL || "";
          const query = dashboardId ? `?dashboardId=${encodeURIComponent(dashboardId)}` : "";
          const response = await fetch(`${base}/api/events/stream${query}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "text/event-stream",
              ...(lastEventId ? { "Last-Event-ID": lastEventId } : {}),
            },
            signal: controller.signal,
          });
          if (response.status === 401 || response.status === 403) return;
          if (!response.ok || !response.body) throw new Error(`SSE status ${response.status}`);

          retry = 1000;
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
            let index;
            while ((index = buffer.indexOf("\n\n")) !== -1) {
              dispatch(buffer.slice(0, index));
              buffer = buffer.slice(index + 2);
            }
          }
        } catch (error) {
          if (controller.signal.aborted) return;
        }
        await wait(retry);
        retry = Math.min(retry * 2, 15000);
      }
    };

    connect();
    return () => {
      stopped = true;
      controller.abort();
    };
  }, [dashboardId]);
}
